import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import FormDrawerWrapper from '../../../../features/components/FormDrawerWrapper'
import EditSectionsList from '../../../../features/components/Forms/EditForm/EditSectionsList'
import NameSectionPreview from '../../../ReusableFormSections/NameSection/NameSectionPreview'
import NameSectionForm from './Sections/NameSection/NameSectionForm'
import TikTokLineItemBudgetSectionPreview from '../../../ReusableFormSections/reusableBudgetSections/TikTokBudgetSections/TikTokLineItemBudgetSection/TikTokLineItemBudgetSectionPreview'
import TikTokLineItemBudgetSectionForm from '../../../ReusableFormSections/reusableBudgetSections/TikTokBudgetSections/TikTokLineItemBudgetSection/TikTokLineItemBudgetSectionForm'
import DatesSectionPreview from './Sections/DatesSection/DatesSectionPreview'
import DatesSectionForm from './Sections/DatesSection/DatesSectionForm'
import ScheduleSectionPreview from './Sections/ScheduleSection/ScheduleSectionPreview'
import ScheduleSectionForm from './Sections/ScheduleSection/ScheduleSectionForm'
import DemographicsSectionPreview from './Sections/DemographicsSection/DemographicsSectionPreview'
import DemographicsSectionForm from './Sections/DemographicsSection/DemographicsSectionForm'
import AudienceSectionPreview from './Sections/AudienceSection/AudienceSectionPreview'
import AudienceSectionForm from './Sections/AudienceSection/AudienceSectionForm'
import LanguagesSectionPreview from './Sections/LanguagesSection/LanguagesSectionPreview'
import LanguagesSectionForm from './Sections/LanguagesSection/LanguagesSectionForm'
import CustomAudiencesSectionPreview from './Sections/CustomAudiencesSection/CustomAudiencesSectionPreview'
import CustomAudiencesSectionForm from './Sections/CustomAudiencesSection/CustomAudiencesSectionForm'
import { getShowCategoryExclusionsDropdowns } from '../../UploadTikTokAdCreative/UploadTikTokAdCreativeForm/LineItemForm/Steps/CustomAudiencesStep/TikTokUploadCreativeAudienceFields/TikTokAdvancedAudienceTargetingSection/helpers'

import useManageDrawer from '../../../../hooks/formHooks/useManageDrawer'
import useManageEditFormData from '../../../../hooks/formHooks/useManageEditFormData'
import usePermissions from '../../../../hooks/usePermissions'
import { useLoadAdditionalData } from './useLoadAdditionalData'

import { insertIf } from '../../../../helpers/common'

import { getLineItem, clearLineItem } from '../../../../modules/actions/lineItems'
import { lineItemSelector, lineItemWasLoadedSelector } from '../../../../modules/selectors/lineItems'
import { selectedTikTokAdAccountSelector } from '../../../../modules/selectors/adAccounts'
import { campaignSelector, campaignWasLoadedSelector } from '../../../../modules/selectors/campaigns'

import { LINE_ITEM_TIKTOK_EDIT } from '../../../../constants/forms'
import { NAME, BUDGET, DATES, SCHEDULE, DEMOGRAPHICS, AUDIENCE, LANGUAGES, CUSTOM_AUDIENCES } from './fields'
import { TIKTOK_PLATFORM } from '../../../../constants/selectLists/platformList'
import { LINE_ITEM_BUDGET_SECTION } from '../../../../constants/permissions'
import { BRAND_SAFETY } from '../../UploadTikTokAdCreative/UploadTikTokAdCreativeForm/LineItemForm/fields'

// here defined initial open states for function LineItemTikTokEdit sections
const sectionsInitialOpenStates = {
  [NAME]: false,
  [BUDGET]: false,
  [DATES]: false,
  [SCHEDULE]: false,
  [DEMOGRAPHICS]: false,
  [AUDIENCE]: false,
  [LANGUAGES]: false,
  [CUSTOM_AUDIENCES]: false
}

const LineItemTikTokEdit = () => {
  const dispatch = useDispatch()

  const permissions = usePermissions()

  const lineItem = useSelector(lineItemSelector)
  const lineItemWasLoaded = useSelector(lineItemWasLoadedSelector)
  const campaignWasLoaded = useSelector(campaignWasLoadedSelector)
  const { id: tiktokAdAccountId } = useSelector(selectedTikTokAdAccountSelector)
  const campaign = useSelector(campaignSelector)
  const { objective: campaignObjective } = campaign

  const { isFormOpen } = useManageDrawer({
    formName: LINE_ITEM_TIKTOK_EDIT
  })

  const showCategoryExclusionsDropdowns = useMemo(() => {
    return getShowCategoryExclusionsDropdowns(campaignObjective, lineItem[BRAND_SAFETY])
  }, [lineItem, campaignObjective])

  const isAdditionalDataLoading = useLoadAdditionalData({ isFormOpen, showCategoryExclusionsDropdowns })

  const isInitialDataLoading = !lineItemWasLoaded || !campaignWasLoaded || isAdditionalDataLoading

  const getLineItemHandler = useCallback(
    selectedEditItemId => {
      dispatch(getLineItem({ id: selectedEditItemId, account: tiktokAdAccountId }, TIKTOK_PLATFORM))
    },
    [dispatch, tiktokAdAccountId]
  )

  const clearLineItemHandler = useCallback(() => {
    dispatch(clearLineItem())
  }, [dispatch])

  useManageEditFormData({
    formName: LINE_ITEM_TIKTOK_EDIT,
    loadedDataId: lineItem.id,
    getDataHandler: getLineItemHandler,
    clearDataHandler: clearLineItemHandler
  })

  // here defined all edit sections for LineItemTikTokEdit
  const editSections = useMemo(
    () => [
      {
        sectionName: NAME,
        title: 'Name',
        PreviewComponent: <NameSectionPreview dataSelector={lineItemSelector} />,
        FormComponent: <NameSectionForm />
      },
      ...insertIf(permissions.can('update', LINE_ITEM_BUDGET_SECTION), {
        sectionName: BUDGET,
        title: 'Budget',
        PreviewComponent: <TikTokLineItemBudgetSectionPreview lineItem={lineItem} />,
        FormComponent: <TikTokLineItemBudgetSectionForm lineItem={lineItem} allowEditBidPrice />
      }),
      {
        sectionName: DATES,
        title: 'Dates',
        PreviewComponent: <DatesSectionPreview />,
        FormComponent: <DatesSectionForm />
      },
      {
        sectionName: SCHEDULE,
        title: 'Schedule',
        PreviewComponent: <ScheduleSectionPreview />,
        FormComponent: <ScheduleSectionForm />
      },
      {
        sectionName: DEMOGRAPHICS,
        title: 'Demographics',
        PreviewComponent: <DemographicsSectionPreview />,
        FormComponent: <DemographicsSectionForm />
      },
      {
        sectionName: AUDIENCE,
        title: 'Audience Targeting',
        PreviewComponent: <AudienceSectionPreview />,
        FormComponent: <AudienceSectionForm />
      },
      {
        sectionName: LANGUAGES,
        title: 'Languages',
        PreviewComponent: <LanguagesSectionPreview />,
        FormComponent: <LanguagesSectionForm />
      },
      {
        sectionName: CUSTOM_AUDIENCES,
        title: 'Custom Audiences',
        PreviewComponent: <CustomAudiencesSectionPreview />,
        FormComponent: <CustomAudiencesSectionForm />
      }
    ],
    [permissions, lineItem]
  )

  return (
    <FormDrawerWrapper title="Edit a TikTok ad group" formName={LINE_ITEM_TIKTOK_EDIT} showOpenButton={false}>
      <EditSectionsList
        editSections={editSections}
        isEditDataLoading={isInitialDataLoading}
        editItemData={lineItem}
        sectionsInitialOpenStates={sectionsInitialOpenStates}
      />
    </FormDrawerWrapper>
  )
}

export default LineItemTikTokEdit
